import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Accordion, AccordionSummary, AccordionDetails, Chip, Alert, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import CustomFormDialog, { FormField } from "../../components/CustomFormDialog/CustomFormDialog";
import CustomPopup from "../../components/CustomPopup/CustomPopup";
import Table, { Column } from "../../components/Table/Table";
import { Delete, Edit, Search, CameraAlt as CameraIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useAlert } from "../../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import { ProductScannerEmbedded } from "../../components/ProductScanner";
import { AggregatedSuggestion } from "../../components/ProductScanner/utils";
import { mapTypeToCategory, generateProductName } from "../../components/ProductScanner/typeMapping";
import { generateProductDescription } from "../../components/ProductScanner/utils";
import { useWarehouse } from "../../contexts/WarehouseContext";
import { productsApi, ProductApiResponse } from "../../services/productsApi";
import { useServerSidePagination } from "../../hooks/useServerSidePagination";
import { PaginatedResponse } from "../../types/api";
import { LabelChip, LabelSelector } from "../../components/Labels";
import CategorySelector from "../../components/CategorySelector/CategorySelector";

interface ProductFormData {
  name: string;
  category: string[];
  description?: string;
  price: number;
  label_ids?: number[];
  shelf_life_days?: number | string | null;
}

const MS_IN_DAY = 24 * 60 * 60 * 1000;

function daysToDateInput(days?: number | null): string {
  if (!days || days <= 0) return "";
  const dt = new Date();
  dt.setHours(0, 0, 0, 0);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().split("T")[0];
}

function dateInputToDays(value?: number | string | null): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value > 0 ? value : null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) {
    const parsedNumber = Number(trimmed);
    return Number.isFinite(parsedNumber) && parsedNumber > 0 ? Math.floor(parsedNumber) : null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((parsedDate.getTime() - today.getTime()) / MS_IN_DAY);
  return diffDays > 0 ? diffDays : null;
}

/** Normalize category to string[] (handles API quirks and customComponent value) */
function normalizeCategory(value: unknown): string[] {
  if (Array.isArray(value) && value.length > 0) {
    return value.map((item) =>
      typeof item === "string" ? item : (item as { name?: string })?.name ?? String(item)
    ).filter(Boolean);
  }
  return Array.isArray(value) ? value.filter((x): x is string => typeof x === "string") : [];
}

const Products = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductApiResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductApiResponse | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    category: [],
    description: "",
    price: 0,
    label_ids: [],
  });
  const [aiSuggestions, setAiSuggestions] = useState<AggregatedSuggestion | null>(null);
  const [fieldsWithAI, setFieldsWithAI] = useState<Set<string>>(new Set());

  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const { selectedWarehouse, loading: warehousesLoading } = useWarehouse();

  // Wrapper function to convert 0-indexed page to 1-indexed for API
  const fetchProducts = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<ProductApiResponse>> => {
      // Don't fetch products until warehouse is loaded and selected
      if (warehousesLoading || !selectedWarehouse?.id) {
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      }

      const response = await productsApi.getProducts({
        page: page + 1, // Convert 0-indexed to 1-indexed for API
        page_size: pageSize,
        search: search || undefined,
        warehouse: selectedWarehouse.id,
      });
      return response;
    },
    [selectedWarehouse?.id, warehousesLoading]
  );

  const pagination = useServerSidePagination(fetchProducts, {
    initialPageSize: 10,
    debounceMs: 500,
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Błąd połączenia z backendem";
      showAlert("error", `Błąd podczas pobierania produktów: ${errorMessage}`, 5000);
      console.error("Błąd podczas pobierania produktów:", error);
    },
  });

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: async (data: ProductFormData & { variants?: Array<{ brand: string; color: string; description: string }> }) => {
      // Get warehouse_id from context or localStorage
      let warehouseId: number | undefined = selectedWarehouse?.id;
      if (!warehouseId) {
        const savedWarehouseId = localStorage.getItem('selectedWarehouseId');
        warehouseId = savedWarehouseId ? Number(savedWarehouseId) : undefined;
      }

      const requestBody: any = {
        name: data.name,
        description: data.description || '',
        category: data.category,
        price: data.price,
      };

      // Add warehouse_id if available
      if (warehouseId) {
        requestBody.warehouse_id = warehouseId;
      }

      // Add variants if provided
      if (data.variants && data.variants.length > 0) {
        requestBody.variants = data.variants;
      }

      // Always send labels array (empty = usuń wszystkie etykiety)
      requestBody.label_ids = data.label_ids ?? [];

      const shelfLife = dateInputToDays(data.shelf_life_days);
      if (shelfLife != null) {
        requestBody.shelf_life_days = shelfLife;
      }

      return await productsApi.createProduct(requestBody);
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', 'Produkt został utworzony');
      setIsDialogOpen(false);
      setFormData({ name: "", category: [], description: "", price: 0 });
      setAiSuggestions(null);
      setFieldsWithAI(new Set());
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.detail || 'Nie udało się utworzyć produktu';
      showAlert('error', message);
    },
  });

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ProductFormData }) => {
      const requestBody: any = {
        name: data.name,
        description: data.description || '',
        category: data.category,
        price: data.price,
      };

      // Always send labels array (empty = usuń wszystkie etykiety)
      requestBody.label_ids = data.label_ids ?? [];

      // Shelf life: null/empty string clears the field, positive numbers set it.
      const shelfLife = dateInputToDays(data.shelf_life_days);
      if (shelfLife === null) {
        requestBody.shelf_life_days = null;
      } else {
        requestBody.shelf_life_days = shelfLife;
      }

      return await productsApi.updateProduct(id, requestBody);
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', 'Produkt został zaktualizowany');
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData({ name: "", category: [], description: "", price: 0 });
      setAiSuggestions(null);
      setFieldsWithAI(new Set());
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.detail || 'Nie udało się zaktualizować produktu';
      showAlert('error', message);
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await productsApi.deleteProduct(id);
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', 'Produkt został usunięty');
      setDeleteDialogOpen(false);
      setSelectedProduct(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.detail || 'Nie udało się usunąć produktu';
      showAlert('error', message);
    },
  });

  // Apply AI suggestions to form
  const applyAISuggestions = useCallback((suggestions: AggregatedSuggestion | null) => {
    if (!suggestions) {
      setAiSuggestions(null);
      setFieldsWithAI(new Set());
      return;
    }

    setAiSuggestions(suggestions);
    const newFieldsWithAI = new Set<string>();

    // Map detectedType to category
    if (suggestions.detectedType) {
      const category = mapTypeToCategory(suggestions.detectedType);
      setFormData(prev => ({ ...prev, category: [category] }));
      newFieldsWithAI.add('category');
    }

    // Generate product name
    const productName = generateProductName(
      suggestions.detectedBrand,
      suggestions.detectedType,
      suggestions.detectedColor
    );
    if (productName && productName !== 'Nowy produkt') {
      setFormData(prev => ({ ...prev, name: productName }));
      newFieldsWithAI.add('name');
    }

    // Generate description
    const description = generateProductDescription(suggestions);
    if (description) {
      setFormData(prev => ({ ...prev, description }));
      newFieldsWithAI.add('description');
    }

    setFieldsWithAI(newFieldsWithAI);
  }, []);

  // Handle form data change from CustomFormDialog
  const handleFormDataChange = useCallback((data: ProductFormData) => {
    setFormData(data);
  }, []);

  // Refresh data when warehouse changes (but only after warehouses are loaded)
  useEffect(() => {
    if (!warehousesLoading && selectedWarehouse?.id !== undefined) {
      // Reset to first page and refresh data
      if (pagination.page !== 0) {
        pagination.handlePageChange(0);
      } else {
        pagination.refresh();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse?.id, warehousesLoading]);

  const columns: Column<ProductApiResponse>[] = [
    {
      id: "name",
      label: t("products.columns.name"),
      flex: 1.5,
    },
    {
      id: "product_code",
      label: t("products.columns.barcode"),
      flex: 1,
      format: (value: string | undefined) => value || "-",
    },
    {
      id: "category",
      label: t("products.columns.category"),
      flex: 1,
      format: (value: string[]) => {
        if (Array.isArray(value) && value.length > 0) {
          return value.join(", ");
        }
        return "-";
      },
    },
    {
      id: "price",
      label: t("products.columns.price"),
      flex: 1,
      format: (value: string) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? "-" : `${numValue.toFixed(2)} zł`;
      },
    },
    {
      id: "quantity",
      label: t("products.columns.quantity"),
      flex: 1,
    },
    {
      id: "labels" as keyof ProductApiResponse,
      label: t("products.columns.labels"),
      flex: 1.5,
      format: (value: any) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return "-";
        }
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", maxWidth: 300 }}>
            {value.map((label: any) => (
              <LabelChip key={label.id} label={label} size="small" />
            ))}
          </Box>
        );
      },
    },
    {
      id: "id" as keyof ProductApiResponse,
      label: t("common.actions"),
      flex: 1.2,
      align: "center",
      sortable: false,
      format: (_, row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <IconButton
            size="small"
            color="primary"
            title={t("common.details")}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/products/${row.id}`);
            }}
          >
            <Search fontSize="small" />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
            size="small"
            color="primary"
            title={t("common.edit")}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProduct(row);
              setDeleteDialogOpen(true);
            }}
            size="small"
            color="primary"
            title={t("common.delete")}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleSaveProduct = (data: ProductFormData) => {
    // Merge dialog form data with labels from state
    const mergedData = { ...data, label_ids: formData.label_ids || [] };

    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: mergedData });
    } else {
      createMutation.mutate(mergedData);
    }
  };

  const handleEditClick = (product: ProductApiResponse) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: normalizeCategory(product.category),
      description: product.description || "",
      price: typeof product.price === "string" ? parseFloat(product.price) : product.price,
      label_ids: product.labels?.map((l: any) => l.id) || [],
      shelf_life_days: daysToDateInput(product.shelf_life_days ?? null),
    });
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
    setFormData({ name: "", category: [], description: "", price: 0, label_ids: [] });
    setAiSuggestions(null);
    setFieldsWithAI(new Set());
  };

  // Product form fields with AI suggestion indicators
  const productFormFields = useMemo(() => {
    return [
      {
        name: "name",
        label: t("products.form.name"),
        required: true,
        aiSuggestion: fieldsWithAI.has('name'),
        aiConfidence: aiSuggestions?.averageConfidence,
      },
      {
        name: "category",
        label: t("products.form.category"),
        required: true,
        aiSuggestion: fieldsWithAI.has('category'),
        aiConfidence: aiSuggestions?.averageConfidence,
        customComponent: ({ value, onChange, error, helperText, label, required }: Parameters<NonNullable<FormField['customComponent']>>[0]) => (
          <CategorySelector
            value={normalizeCategory(value)}
            onChange={onChange}
            label={label}
            required={required}
            error={error}
            helperText={helperText}
          />
        ),
      },
      {
        name: "description",
        label: t("products.form.description") || "Opis",
        multiline: true,
        rows: 3,
        required: false,
        aiSuggestion: fieldsWithAI.has('description'),
        aiConfidence: aiSuggestions?.averageConfidence,
      },
      {
        name: "price",
        label: `${t("products.form.price")} (${t("common.currency")})`,
        type: "number",
        required: true,
      },
      {
        name: "shelf_life_days",
        label: t("products.form.initialExpiryDate"),
        type: "date",
        required: false,
      },
    ];
  }, [t, fieldsWithAI, aiSuggestions]);

  const validateProduct = (data: ProductFormData) => {
    const errors: Record<string, string> = {};
    if (!data.name) errors.name = t("products.validation.nameRequired");
    const category = normalizeCategory(data.category);
    if (category.length === 0)
      errors.category = t("products.validation.categoryRequired");
    const price = typeof data.price === "string" ? parseFloat(data.price) : data.price;
    if (price === undefined || isNaN(price) || price < 0)
      errors.price = t("products.validation.invalidPrice");
    return errors;
  };

  const labelsFormContent = useMemo(() => {
    return (
      <LabelSelector
        value={formData.label_ids || []}
        onChange={(labelIds: number[]) => {
          setFormData((prev) => ({ ...prev, label_ids: labelIds }));
        }}
        label={t("products.form.labels")}
      />
    );
  }, [formData.label_ids, t]);

  // Custom content for form - Labels and ProductScanner (category is in formFields as customComponent)
  const customFormContent = useMemo(() => {
    const scannerContent = editingProduct ? null : (
      <Accordion sx={{
        mt: 2,
        backgroundColor: 'transparent',
        '&:before': {
          display: 'none',
        },
        '& .MuiAccordionSummary-root': {
          color: 'white',
          minHeight: 48,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
        '& .MuiAccordionSummary-content': {
          color: 'white',
          '& .MuiTypography-root': {
            color: 'white',
          },
        },
        '& .MuiAccordionDetails-root': {
          color: 'white',
        },
      }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white !important' }} />}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraIcon sx={{ color: 'white' }} />
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t("products.form.scanProduct")}
            </Typography>
            {aiSuggestions && (
              <Chip
                label={`${Math.round(aiSuggestions.averageConfidence * 100)}%`}
                size="small"
                color={aiSuggestions.averageConfidence > 0.7 ? 'success' : aiSuggestions.averageConfidence > 0.4 ? 'warning' : 'error'}
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <ProductScannerEmbedded
            onSuggestionsChange={applyAISuggestions}
          />
          {aiSuggestions && aiSuggestions.requiresManualReview && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Niska pewność rozpoznania - wymagana ręczna weryfikacja
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    );

    return (
      <Box>
        {labelsFormContent}
        {scannerContent}
      </Box>
    );
  }, [editingProduct, aiSuggestions, applyAISuggestions, labelsFormContent, t]);

  return (
    <Box>
      <Table
        columns={columns}
        data={pagination.data}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalRows={pagination.totalCount}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handlePageSizeChange}
        onSearchChange={pagination.handleSearchChange}
        rowKey="id"
        checkboxSelection={false}
        searchable={true}
        title={t("products.title")}
        description={t("products.description")}
        searchPlaceholder={t("products.searchPlaceholder")}
        loading={pagination.isLoading}
      />

      <CustomFormDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSaveProduct}
        title={
          editingProduct
            ? t("products.editProduct")
            : t("products.addNewProduct")
        }
        submitButtonText={
          editingProduct ? t("common.saveChanges") : t("common.add")
        }
        cancelButtonText={t("common.cancel")}
        formFields={productFormFields}
        initialData={
          editingProduct
            ? {
                name: editingProduct.name,
                category: normalizeCategory(editingProduct.category),
                description: editingProduct.description || "",
                price: typeof editingProduct.price === "string" ? parseFloat(editingProduct.price) : editingProduct.price,
                label_ids: editingProduct.labels?.map((l: any) => l.id) || [],
                shelf_life_days: daysToDateInput(editingProduct.shelf_life_days ?? null),
              }
            : formData
        }
        validate={validateProduct}
        customContent={customFormContent}
        onFormDataChange={handleFormDataChange}
        maxWidth="md"
      />

      <CustomPopup
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("products.deleteConfirmation")}
        text={t("products.deleteWarning")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (selectedProduct) {
            deleteMutation.mutate(selectedProduct.id);
          }
        }}
      />
    </Box>
  );
};

export default Products;
